// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import firebaseApp, { db } from "services/firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { medicationService } from "../services/medicationService";
import { adherenceService } from "../services/adherenceService";
import { differenceInMilliseconds } from "date-fns";
import useIdleTimer from "../hooks/useIdleTimer";
import IdleTimeoutDialog from "../components/auth/IdleTimeoutDialog";
import { invalidateUsageCache } from "../services/usageCache";
import { clearUsageLabelCache } from "../services/usageLabelService";
import {
  isDevEnvironment,
  FAST_IDLE_LOGOUT_IN_DEV,
} from "utils/isDevEnvironment";

const SESSION_START_KEY = "hd_session_start";

// Dev fast-mode: shrink the windows so the logout flow is testable in seconds.
// Never active in production (see FAST_IDLE_LOGOUT_IN_DEV).
const FAST_IDLE = isDevEnvironment() && FAST_IDLE_LOGOUT_IN_DEV;

// Idle window before the warning dialog, and the warning countdown itself.
// Production: 7h55m + 5m = 8h. Dev fast-mode: 20s + 15s = 35s.
const IDLE_CONFIG = FAST_IDLE
  ? { idleMs: 20 * 1000, warningMs: 15 * 1000 }
  : {};

// Absolute cap from session start, and how often we poll it.
const MAX_SESSION_MS = FAST_IDLE ? 2 * 60 * 1000 : 8 * 60 * 60 * 1000;
const ABSOLUTE_CHECK_INTERVAL_MS = FAST_IDLE ? 5 * 1000 : 60 * 1000;

/**
 * How long the sign-in path waits on a forced token refresh before going on without one.
 *
 * ⚠️ A REQUEST THAT NEVER ANSWERS IS NOT A REQUEST THAT FAILS, and only one of those can be
 * caught. A blocked or black-holed call to the token endpoint leaves its promise pending for
 * ever, so a try/catch around it buys nothing — the await simply never returns and the app
 * never finishes loading. The ceiling is what makes that case survivable.
 *
 * Generous, because the cost of tripping it early is a possibly stale claim on a slow network,
 * while the cost of tripping it late is only that the spinner lasts a few seconds longer.
 */
const TOKEN_REFRESH_TIMEOUT_MS = 8000;

/** Resolve with the promise, or resolve anyway once `ms` has passed. */
const withTimeout = (promise, ms) =>
  Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timed out")), ms),
    ),
  ]);

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState();
  // NOTE: "subscription" is misleadingly named - it's actually the user's ROLE (patient, facility, etc.)
  // This is for backwards compatibility. The actual Stripe subscription data is in "subscriptionData"
  const [subscription, setSubscription] = useState();
  // The actual Stripe subscription object with status, created date, etc.
  const [subscriptionData, setSubscriptionData] = useState();
  const [dailyPass, setDailyPass] = useState();
  const [userData, setUserData] = useState();
  const [tokenClaims, setTokenClaims] = useState({});
  const [listings, setListings] = useState({});
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  // Organization data for admin users
  const [organization, setOrganization] = useState(null);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [organizationInvitations, setOrganizationInvitations] = useState([]);
  // null = not initialized, true = loading, false = loaded
  const [organizationLoading, setOrganizationLoading] = useState(true);
  // Medications cache
  const [medications, setMedications] = useState([]);
  const [reminderStatuses, setReminderStatuses] = useState(new Map());
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationsLoadError, setMedicationsLoadError] = useState(false);
  const [remindersLoadError, setRemindersLoadError] = useState(false);
  // Chartmind documents cache (admin-only; live snapshot)
  const [embeddedDocs, setEmbeddedDocs] = useState([]);
  const [clinicalRefs, setClinicalRefs] = useState([]);
  const [chartmindDocsLoading, setChartmindDocsLoading] = useState(false);

  // ============================================
  // 🚨 TEMPORARY FAKE DATA FOR PRESENTATION 🚨
  // ============================================
  // TODO: REMOVE THIS BEFORE PRODUCTION
  const USE_FAKE_TRACKING_DATA = false; // Set to false to disable fake tracking data

  // Generate realistic fake tracking data that varies each reload
  const generateRealisticTrackingData = (
    medicationId,
    medicationName,
    reminderTimes,
    daysBack = 42,
  ) => {
    const dailyData = [];
    const today = new Date();

    // Create different realistic patterns based on medication hash for consistency per med
    const medHash = medicationId.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    // Seed random patterns based on med ID + current day so it changes on reload
    const seed = Math.abs(medHash) + new Date().getDate();
    let pseudoRandom = seed;
    const random = () => {
      pseudoRandom = (pseudoRandom * 9301 + 49297) % 233280;
      return pseudoRandom / 233280;
    };

    // Realistic adherence patterns - people are inconsistent!
    const baseAdherence = 0.7 + random() * 0.25; // Between 70-95%
    const expectedReminders = reminderTimes?.length || 1;

    for (let i = daysBack - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split("T")[0];

      // Weekend effect - people are less consistent on weekends
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      let dayAdherence = baseAdherence;

      if (isWeekend) {
        dayAdherence *= 0.8; // 20% worse on weekends
      }

      // Add some life chaos - random bad/good periods
      if (i > 15 && i < 20 && random() > 0.4) {
        // Bad week - maybe sick or stressed
        dayAdherence *= 0.5;
      } else if (i > 25 && i < 30 && random() > 0.3) {
        // Good streak - motivated period
        dayAdherence = Math.min(1, dayAdherence * 1.3);
      }

      // Some days people completely forget/don't engage (but still show 0 data)
      if (random() < 0.05) {
        dayAdherence = 0; // Complete miss day
      }

      // Daily variation - people aren't robots
      dayAdherence += (random() - 0.5) * 0.3;
      dayAdherence = Math.max(0, Math.min(1, dayAdherence));

      // Calculate realistic taken/missed based on expected reminders
      const maxPossibleTaken = expectedReminders;
      let takenCount = 0;

      // For each expected reminder, roll the dice
      for (let reminder = 0; reminder < maxPossibleTaken; reminder++) {
        if (random() < dayAdherence) {
          takenCount++;
        }
      }

      // Sometimes people take extra or catch up (but not often)
      if (takenCount === maxPossibleTaken && random() > 0.95) {
        // Rare case: took an extra one
        takenCount = maxPossibleTaken;
      }

      const missedCount = maxPossibleTaken - takenCount;

      // ALWAYS generate entries for every day (even if 0 taken)
      const entries = [];

      // Add taken entries
      for (let j = 0; j < takenCount; j++) {
        const hourOffset =
          j * (24 / Math.max(1, expectedReminders)) + random() * 2 - 1; // Spread throughout day with some variation
        entries.push({
          id: `fake_entry_${dateString}_taken_${j}`,
          status: "taken",
          timestamp: new Date(date.getTime() + hourOffset * 60 * 60 * 1000),
          medicationId: medicationId,
        });
      }

      // Add missed entries - be more aggressive about logging misses for presentation
      for (let j = 0; j < missedCount; j++) {
        // Log more missed doses for better presentation data (50% instead of 30%)
        if (random() > 0.5 || takenCount === 0) {
          // Always log if nothing was taken that day
          const hourOffset =
            (takenCount + j) * (24 / Math.max(1, expectedReminders)) +
            random() * 2;
          entries.push({
            id: `fake_entry_${dateString}_missed_${j}`,
            status: "missed",
            timestamp: new Date(date.getTime() + hourOffset * 60 * 60 * 1000),
            medicationId: medicationId,
          });
        }
      }

      // ALWAYS add an entry for every day, even if no explicit entries
      if (entries.length === 0) {
        // If no entries were generated, create a "missed" entry to show 0/X pattern
        entries.push({
          id: `fake_entry_${dateString}_missed_0`,
          status: "missed",
          timestamp: new Date(date.getTime() + 8 * 60 * 60 * 1000), // 8 AM
          medicationId: medicationId,
        });
      }

      dailyData.push({
        date: dateString,
        adherenceRate: maxPossibleTaken > 0 ? takenCount / maxPossibleTaken : 0,
        entries: entries,
        expectedReminders: maxPossibleTaken,
        isRealTrackingDay: true,
      });
    }

    return dailyData;
  };

  // Generate fake tracking data for actual medications
  const generateFakeTrackingDataForRealMeds = (realMedications) => {
    const calendarDataByMedication = new Map();

    // Only generate tracking data for medications with adherence enabled
    const activeMeds = realMedications.filter((med) => med.adherence?.enabled);

    if (activeMeds.length === 0) {
      return {
        dashboardSummary: null,
        calendarDataByMedication,
        loaded: true,
        loading: false,
      };
    }

    activeMeds.forEach((med) => {
      const reminderTimes = med.adherence?.times || ["08:00"];
      const dailyData = generateRealisticTrackingData(
        med.id,
        med.name,
        reminderTimes,
      );
      const last7Days = dailyData.slice(-7); // Get last 7 days

      calendarDataByMedication.set(med.id, {
        dailyData: dailyData,
        isEmpty: dailyData.length === 0,
        medicationName: med.name,
        last7Days: last7Days,
      });
    });

    // Generate dashboard summary using real meds with fake tracking data
    const dashboardSummary = processDashboardSummary(
      calendarDataByMedication,
      activeMeds,
    );

    return {
      dashboardSummary,
      calendarDataByMedication,
      loaded: true,
      loading: false,
    };
  };

  // Tracking data cache - SIMPLE APPROACH for launch
  // 6 weeks: Fast loading, good for charts, covers most user patterns
  // When we scale to thousands of users, we can optimize further
  const [trackingData, setTrackingData] = useState({
    dashboardSummary: null,
    calendarDataByMedication: new Map(),
    loaded: false,
    loading: false,
  });
  const unsubRef = useRef();
  const dailyPassUnsubRef = useRef();
  const subscriptionUnsubRef = useRef();
  const listingsUnsubRef = useRef();
  const invoicesUnsubRef = useRef();
  const organizationUnsubRef = useRef();
  const medicationUnsubRef = useRef();
  const reminderUnsubRef = useRef();
  const trackingUnsubRef = useRef();
  const embeddedDocsUnsubRef = useRef();
  const clinicalRefsUnsubRef = useRef();

  const getData = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .onSnapshot((doc) => {
        // null = doc confirmed missing; undefined = not yet fetched (loading)
        const data = doc.exists ? doc.data() : null;
        setUserData(data);
      });
  };

  const checkSubscriptions = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("subscriptions")
      .onSnapshot((snapshot) => {
        // Find the most recent subscription document
        const subscriptions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (subscriptions.length > 0) {
          // Sort by created date (most recent first) and take the first one
          const latestSubscription = subscriptions.sort(
            (a, b) => (b.created || 0) - (a.created || 0),
          )[0];

          setSubscriptionData(latestSubscription);
        } else {
          setSubscriptionData(null);
        }
      });
  };

  const checkListings = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("listings")
      .onSnapshot((snapshot) => {
        const listingsData = {};
        snapshot.forEach((doc) => {
          const listing = doc.data();
          listingsData[doc.id] = {
            id: doc.id,
            type: listing.type,
            status: listing.status || "pending",
            active: listing.active || false,
            createdAt: listing.createdAt,
            expirationDate: listing.expirationDate,
            zipCode: doc.id,
            // Fields from original zipSubscriptions (featured listings)
            isWinner: listing.type === "featured" ? true : undefined,
            isPaid:
              listing.type === "featured"
                ? listing.status === "active"
                : undefined,
            endDate: listing.endDate,
            winningBid: listing.winningBid,
            // Include any other fields that might exist
            amount_paid: listing.amount_paid,
          };
        });
        setListings(listingsData);
      });
  };

  const checkInvoices = (uid) => {
    return db
      .collection("users")
      .doc(uid)
      .collection("invoices")
      .where("status", "in", ["open", "pending"])
      .onSnapshot((snapshot) => {
        const invoicesData = snapshot.docs.map((doc) => doc.data());
        setInvoices(invoicesData);
      });
  };

  const loadOrganizationTeamData = async (orgId) => {
    if (!orgId) {
      setOrganizationMembers([]);
      setOrganizationInvitations([]);
      return;
    }

    try {
      const fn = firebaseApp
        .functions()
        .httpsCallable("getOrganizationTeamData");
      const { data } = await fn();

      // Defensive check: never hydrate team data if response org doesn't match.
      if (data?.organizationId !== orgId) {
        setOrganizationMembers([]);
        setOrganizationInvitations([]);
        return;
      }

      setOrganizationMembers(Array.isArray(data?.members) ? data.members : []);
      setOrganizationInvitations(
        Array.isArray(data?.invitations) ? data.invitations : [],
      );
    } catch (error) {
      console.error("Error loading organization team data:", error);
      setOrganizationMembers([]);
      setOrganizationInvitations([]);
    }
  };

  const refreshOrganizationTeamData = async () => {
    if (!userData?.organizationId || userData?.role !== "admin") return;
    await loadOrganizationTeamData(userData.organizationId);
  };

  // Set up chartmind document snapshots (embedded docs + clinical refs).
  // Scoped client-side: global admins see scope==global; org admins see their org.
  const setupChartmindDocumentSnapshots = ({ isGlobalAdmin, organizationId }) => {
    setChartmindDocsLoading(true);

    const buildQuery = (rootDocPath) => {
      const base = db.doc(rootDocPath).collection("documents");
      if (isGlobalAdmin) return base.where("scope", "==", "global");
      if (organizationId) return base.where("organizationId", "==", organizationId);
      return null;
    };

    const embQuery = buildQuery("chartdmind/embedded_documents");
    if (embQuery) {
      embeddedDocsUnsubRef.current = embQuery.onSnapshot(
        (snap) => {
          setEmbeddedDocs(
            snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          );
          setChartmindDocsLoading(false);
        },
        (err) => {
          console.error("Error in embedded docs snapshot:", err);
          setChartmindDocsLoading(false);
        },
      );
    }

    const refQuery = buildQuery("chartdmind/clinical_references");
    if (refQuery) {
      clinicalRefsUnsubRef.current = refQuery.onSnapshot(
        (snap) => {
          setClinicalRefs(
            snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          );
        },
        (err) => console.error("Error in clinical refs snapshot:", err),
      );
    }
  };

  // Set up organization data listeners for admin users
  const setupOrganizationSnapshots = (userData) => {
    if (!userData?.organizationId) {
      // No org to load — flip the flag so AdminRoutes can render.
      setOrganizationLoading(false);
      return;
    }

    setOrganizationLoading(true);

    const orgId = userData.organizationId;

    // Wait for BOTH the first org snapshot and team data before clearing loading.
    // Otherwise team data can finish first, AdminRoutes renders with organization
    // still null, and beta admins see a flash of AdminOnboarding.
    let resolveFirstOrgSnapshot;
    const firstOrgSnapshot = new Promise((res) => {
      resolveFirstOrgSnapshot = res;
    });
    const finishFirstSnapshot = () => {
      if (resolveFirstOrgSnapshot) {
        resolveFirstOrgSnapshot();
        resolveFirstOrgSnapshot = null;
      }
    };

    organizationUnsubRef.current = db
      .collection("organizations")
      .doc(orgId)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            setOrganization({
              id: doc.id,
              ...doc.data(),
            });
          } else {
            console.error("Organization document not found:", orgId);
            setOrganization(null);
          }
          finishFirstSnapshot();
        },
        (error) => {
          console.error("Error in organization snapshot:", error);
          setOrganization(null);
          finishFirstSnapshot();
        },
      );

    Promise.all([loadOrganizationTeamData(orgId), firstOrgSnapshot]).finally(
      () => {
        setOrganizationLoading(false);
      },
    );
  };

  // Load medications and reminder statuses with real-time snapshots
  const setupMedicationSnapshots = (uid) => {
    if (!uid) return;

    // A retry calls this again, so drop any live listeners first rather than stack them.
    if (medicationUnsubRef.current) medicationUnsubRef.current();
    if (reminderUnsubRef.current) reminderUnsubRef.current();

    setMedicationsLoading(true);

    // Set up medication snapshot listener
    medicationUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medications")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const meds = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore Timestamps to JS Dates
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
          }));
          setMedications(meds);
          setMedicationsLoadError(false);
          setMedicationsLoading(false);
        },
        (error) => {
          console.error("Error in medication snapshot:", error);
          // Keep any meds already shown and flag the failure, so a failed load
          // never reads as "no medications".
          setMedicationsLoadError(true);
          setMedicationsLoading(false);
        },
      );

    // Set up reminder status snapshot listener
    reminderUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medicationReminders")
      .onSnapshot(
        (snapshot) => {
          const reminderMap = new Map();
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            reminderMap.set(data.medicationId, {
              enabled: data.enabled,
              times: data.times || [],
              timezone: data.timezone,
            });
          });
          setReminderStatuses(reminderMap);
          setRemindersLoadError(false);
        },
        (error) => {
          console.error("Error in reminder snapshot:", error);
          setRemindersLoadError(true);
        },
      );
  };

  // Legacy function for backwards compatibility - now just calls setupMedicationSnapshots
  const loadMedications = (uid) => {
    return setupMedicationSnapshots(uid);
  };

  // Set up smart tracking data snapshot listener
  const setupTrackingDataSnapshot = (uid) => {
    if (!uid) return;

    // Calculate date range for recent tracking data (6 weeks)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 42);

    // Format dates as YYYY-MM-DD for document IDs
    const startDateId = startDate.toISOString().slice(0, 10);
    const endDateId = endDate.toISOString().slice(0, 10);

    // Set up snapshot listener for recent tracking data
    trackingUnsubRef.current = db
      .collection("users")
      .doc(uid)
      .collection("medicationTracking")
      .where(firebase.firestore.FieldPath.documentId(), ">=", startDateId)
      .where(firebase.firestore.FieldPath.documentId(), "<=", endDateId)
      .onSnapshot(
        (snapshot) => {
          // Only trigger reload if we already have data loaded and this is a real change
          if (trackingData.loaded && !snapshot.metadata.fromCache) {
            // Invalidate and reload tracking data
            setTrackingData((prev) => ({ ...prev, loaded: false }));
            // The existing loadTrackingData will be triggered by the useEffect
          }
        },
        (error) => {
          console.error("Error in tracking data snapshot:", error);
        },
      );
  };

  // Medication CRUD operations
  const addMedication = async (medicationData) => {
    if (!user?.uid) throw new Error("User not authenticated");

    try {
      const newMedication = await medicationService.createMedication(
        user.uid,
        medicationData,
      );
      // No optimistic setMedications here: the onSnapshot listener is the source
      // of truth and fires with the new doc. Prepending it as well raced the
      // snapshot and briefly showed the medication twice.

      // Invalidate tracking data cache so new medication gets included
      setTrackingData((prev) => ({ ...prev, loaded: false }));

      return newMedication;
    } catch (error) {
      console.error("Error adding medication:", error);
      throw error;
    }
  };

  const updateMedication = async (medicationId, medicationData) => {
    if (!user?.uid) throw new Error("User not authenticated");

    try {
      await medicationService.updateMedication(
        user.uid,
        medicationId,
        medicationData,
      );
      // The onSnapshot listener reflects the update; no optimistic write needed.

      // Invalidate tracking data cache so changes get reflected
      setTrackingData((prev) => ({ ...prev, loaded: false }));
    } catch (error) {
      console.error("Error updating medication:", error);
      throw error;
    }
  };

  const deleteMedication = async (medicationId) => {
    if (!user?.uid) throw new Error("User not authenticated");

    try {
      await medicationService.deleteMedication(user.uid, medicationId);
      // The onSnapshot listener drops the deleted medication from the list.
      // Clear its reminder status locally (the reminder doc may not be deleted
      // server-side, so the reminder snapshot won't necessarily fire for it).
      setReminderStatuses((prev) => {
        const newMap = new Map(prev);
        newMap.delete(medicationId);
        return newMap;
      });

      // Invalidate tracking data cache so deleted medication gets removed
      setTrackingData((prev) => ({ ...prev, loaded: false }));
    } catch (error) {
      console.error("Error deleting medication:", error);
      throw error;
    }
  };

  // Refresh reminder statuses after adherence changes
  const refreshReminderStatuses = async () => {
    if (!user?.uid) return;

    try {
      const reminders = await adherenceService.getUserReminders(user.uid);
      const reminderMap = new Map();
      reminders.forEach((reminder) => {
        reminderMap.set(reminder.medicationId, {
          enabled: reminder.enabled,
          times: reminder.times || ["08:00"],
          timezone: reminder.timezone,
        });
      });
      setReminderStatuses(reminderMap);
    } catch (error) {
      console.error("Error refreshing reminder statuses:", error);
    }
  };

  // Helper function to get last 7 days
  const getLast7Days = (dailyData) => {
    const days = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split("T")[0];

      // Find data for this date
      const dayData = dailyData.find((d) => d.date === dateString);
      if (dayData) {
        days.push(dayData);
      }
    }

    return days;
  };

  // Load tracking data - LAUNCH COMPROMISE: 6 weeks for performance + accuracy balance
  // Fast for new users, accurate for recent trends, simple architecture
  const loadTrackingData = useCallback(
    async (daysToLoad = 42) => {
      if (!user?.uid || trackingData.loading || trackingData.loaded) {
        return;
      }

      // 🚨 TEMPORARY: Use fake tracking data for presentation
      if (USE_FAKE_TRACKING_DATA) {
        const fakeTrackingData =
          generateFakeTrackingDataForRealMeds(medications);
        setTrackingData(fakeTrackingData);
        return;
      }
      // END TEMPORARY FAKE DATA

      // Process ALL medications, not just those with alerts enabled
      // The filtering should happen at display time, not data loading time
      const allMeds = medications;

      if (allMeds.length === 0) {
        setTrackingData((prev) => ({ ...prev, loaded: true }));
        return;
      }

      try {
        setTrackingData((prev) => ({ ...prev, loading: true }));

        // Get tracking data for all medications
        const calendarDataByMedication = new Map();

        await Promise.all(
          allMeds.map(async (med, index) => {
            try {
              const dailyData = await adherenceService.getDailyTrackingData(
                med.id,
                daysToLoad,
              );

              // Get last 7 days for compact view
              const last7Days = getLast7Days(dailyData || []);

              calendarDataByMedication.set(med.id, {
                dailyData: dailyData || [],
                isEmpty: !dailyData || dailyData.length === 0,
                medicationName: med.name,
                last7Days,
              });
            } catch (error) {
              console.error(
                `Error loading tracking data for ${med.name}:`,
                error,
              );
              calendarDataByMedication.set(med.id, {
                dailyData: [],
                isEmpty: true,
                medicationName: med.name,
                last7Days: [],
              });
            }
          }),
        );

        // Process dashboard summary
        const dashboardSummary = processDashboardSummary(
          calendarDataByMedication,
          allMeds,
        );

        setTrackingData({
          dashboardSummary,
          calendarDataByMedication,
          loaded: true,
          loading: false,
        });
      } catch (error) {
        console.error("Error loading tracking data:", error);
        setTrackingData((prev) => ({
          ...prev,
          loading: false,
          loaded: true, // Prevent infinite retry
        }));
      }
    },
    [
      user?.uid,
      trackingData.loading,
      trackingData.loaded,
      medications, // Use full array to avoid stale closure issues
    ],
  );

  // REMOVED complex lifetime stats loading - keeping it simple for launch
  // When we have thousands of users, we can add back sophisticated optimization

  // Process dashboard summary - moved from hook to context
  const processDashboardSummary = (calendarDataByMedication, medications) => {
    // For dashboard summary, we only calculate stats for medications with alerts enabled
    const activeMeds = medications.filter((med) => med.adherence?.enabled);

    let totalPerfectDays = 0;
    let totalGoodDays = 0;
    let totalPartialDays = 0;
    let totalPoorDays = 0;
    let totalMissedDays = 0;
    let totalResponsesGiven = 0;
    let totalMedicationsTaken = 0; // FIXED: Count actual medications taken, not days
    let adherenceScores = [];
    let allStreaks = [];

    calendarDataByMedication.forEach((calendarData, medId) => {
      if (calendarData.isEmpty || !calendarData.dailyData) {
        return;
      }

      let perfectDays = 0;
      let goodDays = 0;
      let partialDays = 0;
      let poorDays = 0;
      let missedDays = 0;
      let responsesGiven = 0;

      calendarData.dailyData.forEach((day) => {
        const adherenceRate = day.adherenceRate || 0;
        const takenEntries =
          day.entries?.filter((e) => e.status === "taken").length || 0;
        responsesGiven += takenEntries;

        // FIXED: Count actual medications taken for this medication
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          totalMedicationsTaken += takenEntries;
        }

        if (day.entries && day.entries.length > 0) {
          if (adherenceRate >= 1.0) {
            perfectDays++;
          } else if (adherenceRate >= 0.8) {
            goodDays++;
          } else if (adherenceRate >= 0.5) {
            partialDays++;
          } else if (adherenceRate > 0) {
            poorDays++;
          } else {
            missedDays++;
          }
        }
      });

      // Calculate adherence score
      let adherenceScore = 0;
      let totalDosesTaken = 0;
      let totalDosesExpected = 0;

      calendarData.dailyData.forEach((day) => {
        // FIXED: Only count real tracking days, not heatmap filler days
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          const expectedCount = day.expectedReminders;
          const takenCount = day.entries
            ? day.entries.filter((e) => e.status === "taken").length
            : 0;

          totalDosesTaken += takenCount;
          totalDosesExpected += expectedCount;
        }
      });

      if (totalDosesExpected > 0) {
        adherenceScore = Math.round(
          (totalDosesTaken / totalDosesExpected) * 100,
        );
      }

      // Calculate streaks - both current and all historical consecutive streaks
      const daysWithData = calendarData.dailyData
        .filter((day) => day.isRealTrackingDay && day.expectedReminders > 0)
        .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort chronologically for streak analysis

      // Calculate current streak (from most recent day backwards)
      let currentStreak = 0;
      const reverseDays = [...daysWithData].reverse();
      for (const day of reverseDays) {
        if (day.adherenceRate >= 1.0) {
          currentStreak++;
        } else {
          break;
        }
      }

      // Calculate ALL consecutive streaks throughout history
      const allConsecutiveStreaks = [];
      let currentConsecutiveStreak = 0;

      for (const day of daysWithData) {
        if (day.adherenceRate >= 1.0) {
          currentConsecutiveStreak++;
        } else {
          if (currentConsecutiveStreak > 0) {
            allConsecutiveStreaks.push(currentConsecutiveStreak);
            currentConsecutiveStreak = 0;
          }
        }
      }
      // Don't forget the last streak if the data ends on a perfect day
      if (currentConsecutiveStreak > 0) {
        allConsecutiveStreaks.push(currentConsecutiveStreak);
      }

      // Add to totals
      totalPerfectDays += perfectDays;
      totalGoodDays += goodDays;
      totalPartialDays += partialDays;
      totalPoorDays += poorDays;
      totalMissedDays += missedDays;
      totalResponsesGiven += responsesGiven;

      if (adherenceScore > 0) {
        adherenceScores.push(adherenceScore);
      }

      // Store both current streak and all historical streaks
      allStreaks.push({
        currentStreak,
        allConsecutiveStreaks,
        medicationId: medId,
      });
    });

    // FIXED: Use total dose calculation (33%) instead of average percentages (25%)
    // Calculate overall adherence as total taken / total expected across ALL medications
    let grandTotalTaken = 0;
    let grandTotalExpected = 0;

    calendarDataByMedication.forEach((calendarData, medId) => {
      if (calendarData.isEmpty || !calendarData.dailyData) return;

      calendarData.dailyData.forEach((day) => {
        // FIXED: Only count real tracking days for overall calculation
        if (day.isRealTrackingDay && day.expectedReminders > 0) {
          const expectedCount = day.expectedReminders;
          const takenCount = day.entries
            ? day.entries.filter((e) => e.status === "taken").length
            : 0;

          grandTotalTaken += takenCount;
          grandTotalExpected += expectedCount;
        }
      });
    });

    const averageAdherence =
      grandTotalExpected > 0
        ? Math.round((grandTotalTaken / grandTotalExpected) * 100)
        : 0;

    // Filter streaks for medications that have actual tracking data
    const activeStreakData = allStreaks.filter((streakData) => {
      const medData = calendarDataByMedication.get(streakData.medicationId);
      const hasTrackingData =
        medData &&
        !medData.isEmpty &&
        medData.dailyData.some((day) => day.entries && day.entries.length > 0);
      return hasTrackingData;
    });

    // Current streak is the minimum across all active medications (all meds must be consistent)
    const currentStreaks = activeStreakData.map((data) => data.currentStreak);
    const currentStreak =
      currentStreaks.length > 0 ? Math.min(...currentStreaks) : 0;

    // Best streak is the maximum of ALL consecutive streaks throughout history across all medications
    const allHistoricalStreaks = allStreaks.flatMap(
      (data) => data.allConsecutiveStreaks,
    );
    const bestStreak =
      allHistoricalStreaks.length > 0 ? Math.max(...allHistoricalStreaks) : 0;

    // Generate encouragement message
    let message = "";
    if (totalResponsesGiven > 0) {
      const firstDoseDate = Array.from(calendarDataByMedication.values())
        .flatMap((data) => data.dailyData || [])
        .filter((day) => day.adherenceRate > 0)
        .map((day) => new Date(day.date))
        .sort((a, b) => a - b)[0];

      const daysSinceStart = firstDoseDate
        ? Math.ceil((new Date() - firstDoseDate) / (1000 * 60 * 60 * 24))
        : 1;

      if (averageAdherence >= 90) {
        message =
          daysSinceStart >= 30
            ? "Outstanding! Your consistent routine over the past month is paying off."
            : daysSinceStart >= 14
              ? "Excellent work! You're building an amazing medication habit."
              : "Fantastic start! You're already crushing your medication routine.";
      } else if (averageAdherence >= 75) {
        message =
          daysSinceStart >= 21
            ? "Solid work! You've built a strong habit."
            : "Great job! You're staying consistent with your medications.";
      } else if (averageAdherence >= 50) {
        message =
          bestStreak >= 5
            ? "You've had some great streaks! Let's get back to that consistent rhythm."
            : "You're making progress! Consider adding more reminder times or adjusting the schedule to improve.";
      } else {
        message =
          daysSinceStart <= 3
            ? "Every start is a victory! Building a medication routine takes time - you've got this."
            : bestStreak > 0
              ? "You can do this! Let's get back to that streak you had going."
              : "Starting is the hardest part, and you've already begun! Try setting more frequent reminders to build the habit.";
      }
    }

    return {
      averageAdherence,
      totalTaken: totalMedicationsTaken, // TODO(SCALE): When users have 6+ months of data, consider loading full history for lifetime accuracy
      totalMissed: totalPartialDays + totalPoorDays + totalMissedDays,
      bestStreak, // TODO(SCALE): May not reflect true lifetime best if user has data beyond 6-week window
      currentStreak,
      badges: [],
      message,
      totalMedications: activeMeds.length,
    };
  };

  // Clear tracking data when needed
  const clearTrackingData = useCallback(() => {
    setTrackingData({
      dashboardSummary: null,
      calendarDataByMedication: new Map(),
      loaded: false,
      loading: false,
    });
  }, []);

  // Force reset tracking data
  const forceResetTrackingData = () => {
    setTrackingData({
      dashboardSummary: null,
      calendarDataByMedication: new Map(),
      loaded: false,
      loading: false,
    });
  };

  // Sync pending email verification when user logs in
  // This handles the case where email was verified but Firestore update failed
  useEffect(() => {
    const syncPendingEmail = async () => {
      if (!user || !userData) return;

      // Check if there's a pending email that matches Firebase Auth email
      if (
        userData.pendingEmail &&
        user.email &&
        userData.pendingEmail === user.email
      ) {
        console.log("Syncing pending email to Firestore:", user.email);
        try {
          await db.collection("users").doc(user.uid).update({
            email: user.email,
            pendingEmail: firebase.firestore.FieldValue.delete(),
            emailChangeRequested: firebase.firestore.FieldValue.delete(),
          });
          console.log("Successfully synced email to Firestore");
        } catch (err) {
          console.error("Failed to sync email:", err);
        }
      }
    };

    syncPendingEmail();
  }, [user, userData]);

  useEffect(() => {
    /**
     * ⚠️ THIS CALLBACK MUST REACH ITS `setLoading(false)` ON EVERY PATH, INCLUDING THE BAD ONES.
     *
     * `loading` starts true and gates the whole app behind a full-page spinner. It used to be
     * cleared by a bare statement at the end of an async callback with no catch in it, so any
     * rejection between here and there stranded every screen on that spinner with no error, no
     * timeout and no way out but a reload — and a reload signs in again and does the same thing.
     *
     * It surfaced on the phone microphone relay, where the sign-in is a custom token and the
     * forced refresh below is the first network call the new session makes. Anything that
     * blocks or drops that request (a privacy-blocking browser, a phone changing networks)
     * turned "scan the code" into a spinner that never ended.
     */
    let authListener = firebaseApp.auth().onAuthStateChanged(async (user) => {
      setUser(user);

      try {
        if (user) {
          // Anonymous guests do not start a session clock. Nothing enforces the cap
          // for them (see the absolute-expiry effect), and seeding the key would
          // leave a stale start time behind on a shared clinic device for whoever
          // signs in next.
          if (user.isAnonymous) {
            localStorage.removeItem(SESSION_START_KEY);
          } else if (!localStorage.getItem(SESSION_START_KEY)) {
            localStorage.setItem(SESSION_START_KEY, Date.now().toString());
          }

          // Re-enter loading state while we resolve tokens + user doc.
          // Without this, setUser(user) fires synchronously here but userData is
          // still null (from the previous signed-out state), causing a brief
          // redirect to /onboarding before userData arrives.
          setLoading(true);
          setUserData(undefined);
          /**
           * Forced refresh, and deliberately not allowed to be fatal or to hang.
           *
           * It is here so a session that just gained a claim (a custom token's
           * `deviceLinkSecondFactor`, a role set moments ago) is read at its newest rather
           * than from a cached token. Worth asking for, not worth the app for: a refresh that
           * fails or never answers leaves the CACHED token perfectly usable, so it falls
           * through to getIdTokenResult below either way.
           */
          await withTimeout(
            firebaseApp.auth().currentUser?.getIdToken(true),
            TOKEN_REFRESH_TIMEOUT_MS,
          ).catch((err) => {
            console.warn("Forced token refresh failed; using the cached token", err);
          });
          const decodedToken = await firebaseApp
            .auth()
            .currentUser?.getIdTokenResult();

          setTokenClaims(decodedToken?.claims || {});

          let role = decodedToken?.claims?.stripeRole;
          if (decodedToken?.claims?.admin) {
            role = "global_admin";
          }
          setSubscription(role); // This is the ROLE, not subscription data!
          unsubRef.current = getData(user.uid);
          subscriptionUnsubRef.current = checkSubscriptions(user.uid);
          listingsUnsubRef.current = checkListings(user.uid);
          invoicesUnsubRef.current = checkInvoices(user.uid);
          // Medication/tracking snapshots are set up in a separate useEffect that
          // watches userData.role — NOT the token `stripeRole` claim, which is
          // driven by Stripe and is often absent/stale for patients (that gate
          // caused medications to silently stop loading on refresh).
          // Organization snapshots for admins are likewise set up in a separate
          // useEffect that watches userData.role (admin role may not be in token claims).
        } else {
          // ⚠️ EVERY sign-out ends the session clock, not just handleLogout. AuthFlow,
          // MfaGate, JoinPage and others call signOut() directly, and the start time they
          // left behind was inherited by the next account signed in on this browser. Once
          // it was over 8h old, the absolute cap logged that account out the moment it
          // loaded: a patient opening their verification email landed on the sign-in page.
          localStorage.removeItem(SESSION_START_KEY);
          setSubscription(null);
          setSubscriptionData(null);
          setUserData(null);
          setTokenClaims({});
          setDailyPass(null);
          setListings({});
          setInvoices([]);
          // Clear organization data
          setOrganizationMembers([]);
          setOrganizationInvitations([]);
          setOrganizationLoading(null);
          // Clear medication cache
          setMedications([]);
          setReminderStatuses(new Map());
          setMedicationsLoading(false);
          // Clear tracking data cache
          clearTrackingData();
          // Clean up organization snapshots
          if (organizationUnsubRef.current) {
            organizationUnsubRef.current();
            organizationUnsubRef.current = null;
          }
          // Clean up medication and reminder snapshots
          if (medicationUnsubRef.current) {
            medicationUnsubRef.current();
            medicationUnsubRef.current = null;
          }
          if (reminderUnsubRef.current) {
            reminderUnsubRef.current();
            reminderUnsubRef.current = null;
          }
          if (trackingUnsubRef.current) {
            trackingUnsubRef.current();
            trackingUnsubRef.current = null;
          }
          if (embeddedDocsUnsubRef.current) {
            embeddedDocsUnsubRef.current();
            embeddedDocsUnsubRef.current = null;
          }
          if (clinicalRefsUnsubRef.current) {
            clinicalRefsUnsubRef.current();
            clinicalRefsUnsubRef.current = null;
          }
          setEmbeddedDocs([]);
          setClinicalRefs([]);
          setChartmindDocsLoading(false);
        }
      } catch (err) {
        // Whatever failed, the session itself is real — `user` is already set. Carrying on
        // with a screen that may be missing a claim or a snapshot beats a spinner forever.
        console.error("Auth state change handling failed", err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      authListener();
      if (unsubRef.current) unsubRef.current();
      if (dailyPassUnsubRef.current) dailyPassUnsubRef.current();
      if (subscriptionUnsubRef.current) subscriptionUnsubRef.current();
      if (listingsUnsubRef.current) listingsUnsubRef.current();
      if (invoicesUnsubRef.current) invoicesUnsubRef.current();
      if (organizationUnsubRef.current) organizationUnsubRef.current();
      if (medicationUnsubRef.current) medicationUnsubRef.current();
      if (reminderUnsubRef.current) reminderUnsubRef.current();
      if (trackingUnsubRef.current) trackingUnsubRef.current();
      if (embeddedDocsUnsubRef.current) embeddedDocsUnsubRef.current();
      if (clinicalRefsUnsubRef.current) clinicalRefsUnsubRef.current();
    };
  }, []);

  // Set up organization snapshots for admin users (based on userData.role, not token claims)
  useEffect(() => {
    if (!user?.uid || !userData?.role) return;

    if (userData.role === "admin") {
      setupOrganizationSnapshots(userData);
    }

    return () => {
      if (organizationUnsubRef.current) {
        organizationUnsubRef.current();
        organizationUnsubRef.current = null;
      }
    };
  }, [user?.uid, userData?.role, userData?.organizationId]);

  // Set up medication + tracking snapshots for patients (based on userData.role,
  // not the token `stripeRole` claim). Keying on userData.role means the listener
  // starts as soon as the user doc loads, regardless of Stripe claim state —
  // fixing medications that silently failed to reload on refresh.
  useEffect(() => {
    if (!user?.uid || !userData?.role) return undefined;

    if (userData.role === "patient") {
      setupMedicationSnapshots(user.uid);
      setupTrackingDataSnapshot(user.uid);
    }

    return () => {
      if (medicationUnsubRef.current) {
        medicationUnsubRef.current();
        medicationUnsubRef.current = null;
      }
      if (reminderUnsubRef.current) {
        reminderUnsubRef.current();
        reminderUnsubRef.current = null;
      }
      if (trackingUnsubRef.current) {
        trackingUnsubRef.current();
        trackingUnsubRef.current = null;
      }
    };
  }, [user?.uid, userData?.role]);

  // Set up chartmind document snapshots for admins (org admins + global admins).
  // Live updates: components consume embeddedDocs/clinicalRefs directly from context.
  useEffect(() => {
    if (!user?.uid) return undefined;

    const orgId = userData?.organizationId || null;
    const isGlobal = Boolean(tokenClaims?.admin);
    const isOrgAdmin = userData?.role === "admin" && Boolean(orgId);

    if (!isGlobal && !isOrgAdmin) {
      setEmbeddedDocs([]);
      setClinicalRefs([]);
      setChartmindDocsLoading(false);
      return undefined;
    }

    setupChartmindDocumentSnapshots({
      isGlobalAdmin: isGlobal,
      organizationId: orgId,
    });

    return () => {
      if (embeddedDocsUnsubRef.current) {
        embeddedDocsUnsubRef.current();
        embeddedDocsUnsubRef.current = null;
      }
      if (clinicalRefsUnsubRef.current) {
        clinicalRefsUnsubRef.current();
        clinicalRefsUnsubRef.current = null;
      }
    };
  }, [
    user?.uid,
    userData?.role,
    userData?.organizationId,
    tokenClaims?.admin,
  ]);

  const handleLogout = async () => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    if (dailyPassUnsubRef.current) {
      dailyPassUnsubRef.current();
      dailyPassUnsubRef.current = null;
    }
    if (subscriptionUnsubRef.current) {
      subscriptionUnsubRef.current();
      subscriptionUnsubRef.current = null;
    }
    if (listingsUnsubRef.current) {
      listingsUnsubRef.current();
      listingsUnsubRef.current = null;
    }
    if (invoicesUnsubRef.current) {
      invoicesUnsubRef.current();
      invoicesUnsubRef.current = null;
    }
    if (organizationUnsubRef.current) {
      organizationUnsubRef.current();
      organizationUnsubRef.current = null;
    }
    if (medicationUnsubRef.current) {
      medicationUnsubRef.current();
      medicationUnsubRef.current = null;
    }
    if (reminderUnsubRef.current) {
      reminderUnsubRef.current();
      reminderUnsubRef.current = null;
    }
    if (trackingUnsubRef.current) {
      trackingUnsubRef.current();
      trackingUnsubRef.current = null;
    }
    if (embeddedDocsUnsubRef.current) {
      embeddedDocsUnsubRef.current();
      embeddedDocsUnsubRef.current = null;
    }
    if (clinicalRefsUnsubRef.current) {
      clinicalRefsUnsubRef.current();
      clinicalRefsUnsubRef.current = null;
    }
    setEmbeddedDocs([]);
    setClinicalRefs([]);
    setChartmindDocsLoading(false);

    // Clear organization data
    setOrganizationMembers([]);
    setOrganizationInvitations([]);
    setOrganizationLoading(null);
    // Clear medication cache
    setMedications([]);
    setReminderStatuses(new Map());
    setMedicationsLoading(false);
    // Clear tracking data cache
    clearTrackingData();
    // Usage dashboards cache their reads in module scope so revisiting a page costs no reads.
    // That outlives this component, so it has to be dropped here or the next account signing
    // in on a shared workstation sees the previous organization's aggregates and names.
    invalidateUsageCache();
    clearUsageLabelCache();

    localStorage.removeItem(SESSION_START_KEY);
    await firebase.auth().signOut();
  };

  // Absolute session cap (8h in prod) — catches the edge case where a user
  // somehow stays continuously active for an entire day without going idle.
  useEffect(() => {
    // Anonymous guests are exempt, for the reason given on the idle timer below,
    // plus one specific to this cap: SESSION_START_KEY is per-origin and is only
    // set when absent, so on a shared clinic device it still holds the first
    // guest's start time hours later. Without this, every later guest is logged
    // out mid-check-in within a minute of loading the page.
    if (!user || user.isAnonymous) return;

    const checkAbsoluteExpiry = () => {
      const sessionStart = localStorage.getItem(SESSION_START_KEY);
      if (!sessionStart) return;
      const elapsedMs = differenceInMilliseconds(new Date(), new Date(parseInt(sessionStart, 10)));
      if (elapsedMs > MAX_SESSION_MS) {
        handleLogout();
      }
    };

    checkAbsoluteExpiry();
    const interval = setInterval(checkAbsoluteExpiry, ABSOLUTE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isWarning, countdown, totalWarningSeconds, extendSession } = useIdleTimer({
    onIdle: () => {
      // Stash the email so the login screen can drop them straight onto the
      // password step instead of re-typing it. Cleared by AuthPage on read.
      if (user?.email) sessionStorage.setItem("hd_logout_email", user.email);
      sessionStorage.setItem("hd_logout_reason", "idle");
      handleLogout();
    },
    // Not for anonymous guests. There is nothing behind an anonymous session worth
    // protecting with an idle timeout, and logging one out is unrecoverable — the
    // account cannot be signed back into, so a guest half way through VisitStart
    // loses their answers outright.
    enabled: Boolean(user) && !user.isAnonymous,
    ...IDLE_CONFIG,
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        subscription, // This is the user ROLE (patient, facility, etc.) - confusing naming for backwards compatibility
        subscriptionData, // This is the actual Stripe subscription object with status
        tokenClaims,
        // Platform super-admin: JWT custom claim only (not Firestore role).
        isGlobalAdmin: Boolean(tokenClaims?.admin),

        userData,
        listings,
        invoices,
        userLoading: loading,
        logout: handleLogout,

        // Medication management
        medications,
        reminderStatuses,
        medicationsLoading,
        medicationsLoadError,
        remindersLoadError,
        addMedication,
        updateMedication,
        deleteMedication,
        refreshReminderStatuses,
        loadMedications,

        // Tracking data management
        trackingData,
        loadTrackingData,
        clearTrackingData,
        forceResetTrackingData, // DEBUG function

        // Organization data (for admin users)
        organization,
        organizationMembers,
        organizationInvitations,
        organizationLoading,
        refreshOrganizationTeamData,

        // Chartmind documents (live, admin-only)
        embeddedDocs,
        clinicalRefs,
        chartmindDocsLoading,
      }}
    >
      {children}
      <IdleTimeoutDialog
        open={isWarning}
        countdown={countdown}
        totalWarningSeconds={totalWarningSeconds}
        onStay={extendSession}
        onLogout={handleLogout}
      />
    </AuthContext.Provider>
  );
};
